import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import "../../Assets/Styles/pages/uploadImage.scss";
import { useTranslation } from "react-i18next";
import CancelButton from "../CancelButton/Cancel_button";
import ImageType from "../Cards/imageType";
import ImageDimensions from "../Cards/imageDimension";
import ImageQuality from "../Cards/imageQuality";
import { setCreate } from "../../Redux/DownloadSlice/downloadSlice";
import helper_functions from "../../Config/helper_functions";
import ButtonRounded from "../Signature/button";

interface DownloadImageProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  setLoaderParent?: (value: boolean) => void;
  setOpenProgress: (value: boolean) => void;
}

const DownloadImage: React.FC<DownloadImageProps> = ({
  open,
  setOpen,
  setLoaderParent,
}) => {
  const [loader, setLoader] = useState(false);
  const [landscape, setLandscape] = useState(false);
  const [image, setImage] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { t } = useTranslation();

  const modalRef = useRef<HTMLDivElement>(null);

  const downloadState = useSelector((state) => state["downloadSlice"]);

  const dispatch = useDispatch();

  /** Match BatchDownload / canvas: only scale when a positive preset is chosen; "" and "0" mean maintain original size. */
  const resolutionNum = Number(downloadState.Resolution);
  const hasPresetResolution =
    Number.isFinite(resolutionNum) && resolutionNum > 0;

  const setDownload = async () => {
    if (!downloadState.Image) return;

    const targetFormat = downloadState.Format
      ? downloadState.Format
      : downloadState.Extension;

    let srcW = downloadState.mainImageWidth;
    let srcH = downloadState.mainImageHeight;
    try {
      const d = await helper_functions.getImageDimensionsFromDataUrl(downloadState.Image);
      if (d.width > 0 && d.height > 0) {
        srcW = d.width;
        srcH = d.height;
      }
    } catch {
      // keep Redux dimensions
    }

    let targetWidth = srcW;
    let targetHeight = srcH;

    if (hasPresetResolution && srcW > 0) {
      targetWidth = resolutionNum;
      targetHeight = (resolutionNum / srcW) * srcH;
    } else if (!hasPresetResolution) {
      const ow = downloadState.mainImageWidth;
      const oh = downloadState.mainImageHeight;
      if (ow > 0 && oh > 0) {
        targetWidth = ow;
        targetHeight = oh;
      }
    }

    const sizeMismatch =
      Math.abs(targetWidth - srcW) > 0.5 ||
      Math.abs(targetHeight - srcH) > 0.5;

    // If the user clicks before the async resize effect finishes, force the resize now.
    // (Preset scaling or maintain-mode upscale from preview to original size.)
    if (sizeMismatch && image === downloadState.Image) {
      const resized = await helper_functions.resizeFile(
        downloadState.Image,
        targetWidth,
        targetHeight,
        targetFormat,
        downloadState.Quality
      );
      setImage(resized);
      setLandscape(targetHeight <= targetWidth);
      const link = document.createElement("a");
      link.href = resized;
      // if preserve file format, then download as original format
      link.download = `downloaded_image.${downloadState.Format === 0 ? downloadState.Extension : downloadState.Format}`;
      link.click();
      setSnackbarOpen(true);
      return;
    }

    const downloadSrc = image ?? downloadState.Image;
    if (!downloadSrc) return;
    
    const link = document.createElement("a");
    link.href = downloadSrc;
    link.download = `downloaded_image.${downloadState.Format === 0 ? downloadState.Extension : downloadState.Format}`;
    link.click();
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (open) {
    setTimeout(() => {
        setDisabled(false);
      }, 5000);
    }
  },[open])

  useEffect(() => {
    setLoader(downloadState.Create);
  }, [downloadState.Create]);

  useEffect(() => {
    const effectFunc = async () => {
      if (downloadState.Image) {
        let srcW = downloadState.mainImageWidth;
        let srcH = downloadState.mainImageHeight;
        try {
          const d = await helper_functions.getImageDimensionsFromDataUrl(downloadState.Image);
          if (d.width > 0 && d.height > 0) {
            srcW = d.width;
            srcH = d.height;
          }
        } catch {
          // keep Redux dimensions
        }

        let width: number;
        let height: number;

        if (hasPresetResolution) {
          width = resolutionNum;
          height = srcW > 0 ? (width / srcW) * srcH : srcH;
        } else {
          const ow = downloadState.mainImageWidth;
          const oh = downloadState.mainImageHeight;
          if (ow > 0 && oh > 0) {
            width = ow;
            height = oh;
          } else {
            width = srcW;
            height = srcH;
          }
        }

        const img = await helper_functions.resizeFile(
          downloadState.Image,
          width,
          height,
          downloadState.Format ? downloadState.Format : downloadState.Extension,
          downloadState.Quality
        );

        // Use the resized output for the modal preview.
        setImage(img);
        setLandscape(height <= width);
      }
    };

    effectFunc();
  }, [
    downloadState.Image,
    downloadState.Resolution,
    downloadState.mainImageWidth,
    downloadState.mainImageHeight,
    downloadState.Format,
    downloadState.Quality,
    hasPresetResolution,
  ]);

  useEffect(() => {
    if (open) {
      setLoaderParent?.(false);
      dispatch(setCreate(true));
    }
  }, [open]);

  useEffect(() => {
    setImage(downloadState.Image);
    setLoaderParent(false);
  }, [downloadState.Image]);

  const handleClose = () => {
    setOpen(false);
    setDisabled(true);
    dispatch(setCreate(false));
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => {
          handleClose();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Fade in={open}>
          <Row ref={modalRef} className="model p-0 m-0">
            <CancelButton close={() => handleClose()} />
            <Col className="mt-4 pt-1">
              <Row>
                <Row className="d-flex justify-content-center productSansBold text_18 line_height_100pc black_0C0C0C">
                  {t("DownloadImage.Title")}
                </Row>
                <Row className="productSansLight text_15 line_height_100pc pb-1 mt-2 d-flex justify-content-center">
                  {t("DownloadImage.Discription")}
                </Row>
              </Row>
              <Col className="border-top my-4 pb-1 mx-4">
                <Row className="d-flex pb-1 align-items-center justify-content-center">
                  <Col className="h-100 d-flex flex-column align-items-center justify-content-center">
                    <img
                      alt="template"
                      className={`${landscape ? "w-100" : "w-75"} mt-4`}
                      src={image}
                    />
                  </Col>
                  <Col>
                    <Col className="mt-4 pt-1">
                      <ImageType downloadState={downloadState} />
                    </Col>
                    <Col className="mt-3">
                      <ImageDimensions downloadState={downloadState} />
                    </Col>
                    <Col className="mt-3">
                      <ImageQuality downloadState={downloadState} />
                    </Col>
                  </Col>
                </Row>
                <ButtonRounded
                  onclick={() => setDownload()}
                  disabled={disabled}
                  text={"DownloadImage.Download"}
                  style={"w_edit_button continue_button  mt-4"}
                  loader={loader}
                />
              </Col>
            </Col>
          </Row>
        </Fade>
      </Modal>
      {ReactDOM.createPortal(
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity="success"
            variant="filled"
            sx={{ maxWidth: "500px", width: "auto" }}
          >
            {t("DownloadImageProgress.SuccessfullyDownloaded")}
          </Alert>
        </Snackbar>,
        document.body
      )}
    </>
  );
};

export default DownloadImage;
